import { View, RadioGroup, Label, YStack } from 'tamagui'
import { QuestionWrapper } from './QuestionWrapper'
import { useFormContext, Controller } from 'react-hook-form'
import { useCallback } from 'react'
import { IExamQuestionOption } from '@dtos/Activity'

interface IMultipleChoiceQuestionProps {
  options: IExamQuestionOption[]
  questionId: string
  previousAnswer?: {
    selectedOption: string
    correct: boolean
    score: number
  }
  mode: 'edit' | 'readonly' | 'retry'
}

export function MultipleChoiceQuestion({
  options,
  questionId,
  previousAnswer,
  mode,
}: IMultipleChoiceQuestionProps) {
  const { control } = useFormContext()

  const getColor = useCallback(
    (value: string, ref: string, isTransparent = false) => {
      if (value === ref) {
        if (!previousAnswer) return '$primary-300'
        if (previousAnswer?.correct) return '$success-300'
        return '$danger-300'
      }

      return isTransparent ? 'transparent' : '$neutral-300'
    },
    [previousAnswer],
  )

  return (
    <View>
      <Controller
        name={questionId}
        control={control}
        defaultValue={previousAnswer?.selectedOption || ''}
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            name={`multiple-choice-${questionId}`}
            onValueChange={mode === 'edit' ? onChange : undefined}
            value={value}
            disabled={mode === 'readonly' || mode === 'retry'}
          >
            <YStack gap="$spacing-inset-nano">
              {options.map((option) => (
                <QuestionWrapper
                  key={option.id}
                  color={getColor(value, option.id)}
                >
                  <View flexDirection="row" alignItems="center">
                    <RadioGroup.Item
                      value={option.id}
                      id={`${option.id}-${questionId}`}
                      borderColor="$neutral-200"
                      backgroundColor={getColor(value, option.id, true)}
                      size={'$spacing-lg'}
                    >
                      <RadioGroup.Indicator
                        borderWidth={1}
                        w={8}
                        h={8}
                        borderColor={getColor(value, option.id)}
                        backgroundColor={
                          value === option.id ? '$neutral-100' : 'transparent'
                        }
                      />
                    </RadioGroup.Item>
                    <Label
                      color="$neutral-800"
                      fontFamily="$mulishRegular"
                      size={'$font-size-xxs'}
                      htmlFor={`${option.id}-${questionId}`}
                      p="$spacing-inset-xs"
                      flex={1}
                    >
                      {option.title}
                    </Label>
                  </View>
                </QuestionWrapper>
              ))}
            </YStack>
          </RadioGroup>
        )}
      />
    </View>
  )
}
